<template>
  <div>
    <BubbleHeader :bubble="bubble" />
    <div class="pt-6 px-4 sm:px-6 md:px-8">
      <div v-if="!bubble.offline" class="mb-6 p-4 text-center text-gray-600">
        <p>Content needs to be downloaded in order to view images and videos.</p>
      </div>
      <div v-else class="flex flex-col lg:flex-row gap-6 max-w-[1200px] mx-auto">
        <!-- Left column: Media content -->
        <div class="w-full lg:w-[65%]">
          <MediaGallery :items="mediaItems" :bubble-id="bubble.uuid" />
        </div>

        <!-- Right column: Info and details -->
        <div class="w-full lg:w-[35%]">
          <Card class="p-4 sticky top-4">
            <div v-if="bubble.author && bubble.offline" class="mb-4">
              <div class="flex items-center">
                <template v-if="authorPicSrc">
                  <NuxtImg
                    :src="authorPicSrc"
                    :alt="bubble.author[0]"
                    class="w-10 h-10 rounded-full mr-3"
                  />
                </template>
                <Icon v-else name="heroicons:user-circle" class="w-10 h-10 text-gray-400 mr-3" />
                <div>
                  <a
                    :href="`https://www.instagram.com/${bubble.authorHandle}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-semibold hover:underline"
                  >
                    {{ bubble.author[0] }}
                  </a>
                  <p class="text-sm text-gray-500">@{{ bubble.authorHandle }}</p>
                </div>
              </div>
            </div>
            <Separator class="my-4" />
            <div class="mb-4">
              <p class="text-sm text-gray-700">Saved on {{ formatDate(bubble.createdAt) }}</p>
            </div>
            <div v-if="bubble.caption" class="mb-6">
              <p class="whitespace-pre-wrap text-sm">{{ formattedCaption }}</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { InstagramBubble } from '@/stores/interfaces'
import BubbleHeader from '../BubbleHeader.vue'
import { useRuntimeConfig } from '#app'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Card } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { useLogger } from '@/composables/useLogger'
import MediaGallery from '@/components/ui/media-gallery/MediaGallery.vue'
import type { MediaItem } from '@/components/ui/media-gallery/MediaGallery.vue'
import { useTypeNavigation } from '@/composables/useTypeNavigation'

declare global {
  interface Window {
    navigateToHashtag: (tag: string) => void
  }
}

const {
  public: { dataStore },
} = useRuntimeConfig()
const router = useRouter()
const logger = useLogger()

const props = defineProps<{
  bubble: InstagramBubble
}>()

// Use the type navigation composable
useTypeNavigation(props.bubble)

// Fix type safety in sorting functions
const sortByNumber = (a: string, b: string): number => {
  const numA = parseInt((a || '').split('_')[1] || '0')
  const numB = parseInt((b || '').split('_')[1] || '0')
  return numA - numB
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function getAuthorPicSrc(bubble: InstagramBubble) {
  if (bubble.offline && bubble.offlineMediaResources?.author_pic) {
    return `${dataStore}${bubble.offlineMediaResources.author_pic}`
  }
  return bubble.authorPic
}

const authorPicSrc = computed(() => getAuthorPicSrc(props.bubble))

const formattedCaption = computed(() => {
  if (!props.bubble.caption) return ''
  return props.bubble.caption
})

interface MediaResources {
  [key: string]: string | undefined
}

const mediaItems = computed(() => {
  const items: MediaItem[] = []
  if (props.bubble.offline && props.bubble.offlineMediaResources) {
    const resources = props.bubble.offlineMediaResources as MediaResources

    // Helper function to safely concatenate paths
    const getFullPath = (path: string | undefined): string | undefined => {
      return path ? `${dataStore}${path}` : undefined
    }

    // Get all keys from offlineMediaResources
    const entries = Object.entries(resources)

    // Log video entries
    const videoEntries = entries.filter(
      ([key]) => key.startsWith('video_') && !key.includes('cover_photo')
    )
    logger.debug(
      '🎯 Found video entries:',
      videoEntries.map(([key]) => key)
    )

    // Process videos
    videoEntries
      .sort(([a], [b]) => sortByNumber(a, b))
      .forEach(([_key, value]) => {
        if (value) {
          const fullPath = getFullPath(value)
          const coverPhotoPath = getFullPath(resources.cover_photo)
          if (fullPath) {
            items.push({
              type: 'video',
              src: fullPath,
              cover_photo: coverPhotoPath,
            })
          }
        }
      })

    // Log image entries
    const imageEntries = entries.filter(
      ([key]) => key.startsWith('image_') && !key.includes('cover_photo')
    )
    logger.debug(
      '🎯 Found image entries:',
      imageEntries.map(([key]) => key)
    )

    // Process images
    imageEntries
      .sort(([a], [b]) => sortByNumber(a, b))
      .forEach(([_key, value]) => {
        if (value) {
          const fullPath = getFullPath(value)
          if (fullPath) {
            items.push({
              type: 'image',
              src: fullPath,
            })
          }
        }
      })
  } else if (props.bubble.mediaResources) {
    if (props.bubble.mediaResources.video) {
      items.push({
        type: 'video',
        src: props.bubble.mediaResources.video,
        cover_photo: props.bubble.coverPhoto || undefined,
      })
    } else if (props.bubble.mediaResources.images) {
      props.bubble.mediaResources.images.forEach((image) => {
        items.push({
          type: 'image',
          src: image,
        })
      })
    }
  }

  logger.debug(
    '🎯 Final media items:',
    items.map((item) => ({ type: item.type, index: items.indexOf(item) }))
  )
  return items
})

function navigateToHashtag(tag: string) {
  router.push({ name: 'instagram', query: { hashtag: tag } })
}

if (typeof window !== 'undefined') {
  window.navigateToHashtag = navigateToHashtag
}
</script>

<style scoped>
/* No styles needed as they're moved to MediaGallery component */
</style>
